import Home from "pages/Home";
import SearchTypeSelector from "pages/SearchTypeSelector";
import SpecificationGroup from "pages/SpecificationGroup";
import EngineModelGroup from "pages/EngineModelGroup";
import Catalyst from "pages/Catalyst";
import Results from "pages/Results";
import NotFound from "pages/NotFound";
import Disclaimer from "pages/Disclaimer";

import T from "i18n";

import { useLocation } from "react-router-dom";
import queryString from "query-string";
import GasType from "pages/GasType";
import { useManufacturers, useType } from "../api/hooks";

// hooks to compute the BreadCrumbs
function useManufacturerBC() {
  const location = useLocation();
  const search = queryString.parse(location.search);
  const manufacturerid = parseInt(search.manufacturerid, 10);
  const iframe = search.iframe === "true";

  const { data } = useManufacturers();

  const name = data?.find((m) => m.id === manufacturerid)?.name;

  return { label: name, path: `/?iframe=${iframe}` };
}

function useEngineModelBC() {
  const location = useLocation();
  const {
    enginetypeid,
    manufacturerid,
    groupid,
    iframe = false,
  } = queryString.parse(location.search);

  const { data } = useType(enginetypeid);

  return {
    label: data?.engine_series ?? data?.specification,
    path: `/engine_model_group?manufacturerid=${manufacturerid}&groupid=${groupid}&iframe=${iframe}`,
  };
}

function useSearchTypeBc() {
  const location = useLocation();
  const { manufacturerid, groupid, iframe = false } = queryString.parse(
    location.search
  );

  const { data } = useManufacturers();
  const mId = parseInt(manufacturerid, 10);
  const gId = parseInt(groupid, 10);

  const oem = data?.find((m) => m.id === mId);

  const label =
    oem?.engine_types_id === gId
      ? T({ path: "searchTypeSelector.engineType" })
      : T({ path: "searchTypeSelector.specification" });

  // if we have both the option then we surely have asked the user which one to use
  if (oem?.engine_types_id && oem?.specifications_id) {
    return {
      label,
      path: `/search_selector?manufacturerid=${manufacturerid}&iframe=${iframe}`,
    };
  }

  // otherwise we skipeed the step, hence no need to show it

  return {
    label: undefined,
  };
}

function useGasTypeBC() {
  const location = useLocation();
  const {
    enginetypeid,
    manufacturerid,
    groupid,
    gastype,
    iframe = false,
  } = queryString.parse(location.search);

  const { data } = useType(enginetypeid);

  if (data?.skip_gas_type !== true) {
    return {
      label: gastype,
      path: `/gas_type?manufacturerid=${manufacturerid}&groupid=${groupid}&enginetypeid=${enginetypeid}&iframe=${iframe}`,
    };
  }
  
  // otherwise we skipeed the step, hence no need to show it

  return {
    label: undefined,
  };
}

function useCatalystBC() {
  const location = useLocation();
  const {
    enginetypeid,
    manufacturerid,
    groupid,
    gastype,
    catalyst,
    iframe = false,
  } = queryString.parse(location.search);

  const { data } = useType(enginetypeid);
  const products = data?.types.filter((p) => p.name === gastype);
  const showBC = products?.length > 1;

  return {
    label: showBC && (catalyst === "true" ? "Catalyst" : "No catalyst"),
    path: `/catalyst?manufacturerid=${manufacturerid}&groupid=${groupid}&enginetypeid=${enginetypeid}&gastype=${gastype}&iframe=${iframe}`,
  };
}

// map object of the routes
export default new Map([
  ["/", { element: Home, breadcrumbs: [] }],
  ["/disclaimer", { element: Disclaimer, breadcrumbs: [] }],
  [
    "/search_selector",
    {
      element: SearchTypeSelector,
      breadcrumbs: [
        useManufacturerBC,
        () => ({ label: T({ path: "searchTypeSelectorPage.name" }) }),
      ],
    },
  ],
  [
    "/engine_model_group",
    {
      element: EngineModelGroup,
      breadcrumbs: [
        useManufacturerBC,
        useSearchTypeBc,
        () => ({ label: T({ path: "engineModelGroupPage.name" }) }),
      ],
    },
  ],
  [
    "/specification_group",
    {
      element: SpecificationGroup,
      breadcrumbs: [
        useManufacturerBC,
        useSearchTypeBc,
        () => ({ label: T({ path: "specificationGroupPage.name" }) }),
      ],
    },
  ],
  [
    "/gas_type",
    {
      element: GasType,
      breadcrumbs: [
        useManufacturerBC,
        useSearchTypeBc,
        useEngineModelBC,
        () => ({ label: T({ path: "gasTypePage.name" }) }),
      ],
    },
  ],
  [
    "/catalyst",
    {
      element: Catalyst,
      breadcrumbs: [
        useManufacturerBC,
        useSearchTypeBc,
        useEngineModelBC,
        useGasTypeBC,
        () => ({ label: T({ path: "catalystPage.name" }) }),
      ],
    },
  ],
  [
    "/results",
    {
      element: Results,
      breadcrumbs: [
        useManufacturerBC,
        useSearchTypeBc,
        useEngineModelBC,
        useGasTypeBC,
        useCatalystBC,
        () => ({ label: T({ path: "resultsPage.name" }) }),
      ],
    },
  ],
  ["*", { element: NotFound }],
]);
